/**
 * Atlas access-list thresholds and the entry-state contract.
 *
 * These numbers decide what the UI shouts about AND what the resolver reports, so
 * they live in one place. A banner that warns at 180 while the API classifies at
 * 190 is not a cosmetic bug — it is a page that tells you two different things
 * about whether production is about to lose database access.
 */

/**
 * The hard MongoDB Atlas limit on entries in a project's IP access list. Not a
 * budget we chose: past this, `POST …/accessList` is REJECTED, so the next node to
 * come up never reaches the database. Every capacity bar is `n / ATLAS_CAP`, never
 * normalised to the largest project — normalising would make the cap invisible,
 * which is the whole failure this feature exists to prevent.
 */
export const ATLAS_CAP = 200;

/** Amber. Early enough that there is still room to clean up before adds fail. */
export const ATLAS_WARN = 180;

/** Red. Effectively out of room — a single node pool resize can clear this. */
export const ATLAS_CRIT = 195;

/**
 * The hard DigitalOcean limit on trusted-source rules per managed database (DO docs:
 * "clusters are limited to 100 firewall rules"). A `do_db` target (migration 052) is
 * measured against THIS cap, never ATLAS_CAP — the two numbers describe two different
 * products and drawing a DO firewall on a 200-slot bar would hide the cap it actually
 * has. Owner decision e-10 (2026-09-04): show `n / 100` with the same warn/crit idea.
 */
export const DO_DB_RULE_CAP = 100;
export const DO_DB_WARN = 90;
export const DO_DB_CRIT = 97;

/** Which access-list product a target row is. See `atlas_projects.kind` (052). */
export type TargetKind = 'atlas' | 'do_db';

/** The cap and thresholds for one target kind — the ONE place a capacity bar, an
 *  alert and a resolver may read them from. Unknown kinds fall back to Atlas so a row
 *  written by a newer server never renders with no cap at all. */
export function capacityFor(kind: TargetKind | string | null | undefined): {
  cap: number;
  warn: number;
  crit: number;
} {
  if (kind === 'do_db') return { cap: DO_DB_RULE_CAP, warn: DO_DB_WARN, crit: DO_DB_CRIT };
  return { cap: ATLAS_CAP, warn: ATLAS_WARN, crit: ATLAS_CRIT };
}

/**
 * THE FLEET HAS TWO CLOCKS AND NO OTHERS (owner decision, Binh, 2026-08-25).
 *
 * `AGENT_YELLOW_MINUTES` (10) warns and `AGENT_RED_MINUTES` (15) red-flags, both in
 * `shared-types/agents.ts`, and a HUMAN decides from the Overview checklist whether to
 * delete the agent or recover the node. Four thresholds that used to live here are gone:
 *
 *   ATLAS_AGENT_STALE_MIN (30 min)  the per-project freshness signal — now the red clock.
 *   AGENT_GONE_HOURS (4 h)          "is this node dead?" — now the red clock. A second,
 *                                   slower answer to the same question is a second answer.
 *   ATLAS_ENTRY_STALE_DAYS (30 d)   classified an entry `stale`, i.e. DELETABLE, on the
 *                                   age of a row written at REGISTER time. It appeared on
 *                                   no screen, so nothing an operator could see explained
 *                                   why an address was suddenly offered for reclaim.
 *   isAgentGone()                   the predicate over AGENT_GONE_HOURS.
 *
 * Anything that needs to ask "is this node alive right now" imports `agentStaleLevel` or
 * `AGENT_RED_MINUTES`. Do not add a third clock here.
 */

export type AtlasLevel = 'ok' | 'warn' | 'crit';

/** `null` = never read (no credentials). Unknown is NOT healthy — the caller must
 *  render it as unknown rather than falling through to the ok branch. */
export function atlasLevel(
  live: number | null | undefined,
  kind: TargetKind | string | null = 'atlas',
): AtlasLevel | null {
  if (live == null) return null;
  const { warn, crit } = capacityFor(kind);
  if (live >= crit) return 'crit';
  if (live >= warn) return 'warn';
  return 'ok';
}

/**
 * Where an entry in the live Atlas list came from, and whether anything still
 * stands behind it. This is the diff the old two-table page made the operator do in
 * their head.
 *
 *  - `in_sync`   — in Atlas, and an agent reports it. The healthy case.
 *  - `no_agent`  — in Atlas, nothing reports it. Occupies a slot for nothing; this
 *                  is what "reclaimable" counts.
 *  - `stale`     — every agent behind this address has been silent past the RED clock
 *                  (AGENT_RED_MINUTES). Probably a dead node, but the agent's silence
 *                  could also be the cause, so it is not the same finding as `no_agent`.
 *  - `manual`    — added by hand in Atlas, outside this system. Legitimate (bastions,
 *                  offices) and must never be reported as reclaimable.
 *  - `open_to_all` — a `/0` CIDR: the list is open to the internet, so the other 199
 *                  entries are decoration. Not in the original state table; it came
 *                  out of the prototype's own fixtures and is too important to drop.
 */
export type AtlasEntryState = 'in_sync' | 'no_agent' | 'stale' | 'manual' | 'open_to_all';

/** A CIDR that admits every address. `/0` is the only prefix that can do it, in
 *  either family, so the suffix test is exact rather than a heuristic. */
export function isOpenToAll(cidr: string): boolean {
  return cidr.trim().endsWith('/0');
}

export const ATLAS_ENTRY_STATE_LABELS: Record<AtlasEntryState, string> = {
  in_sync: 'in sync',
  no_agent: 'no agent',
  stale: 'stale',
  manual: 'manual',
  open_to_all: 'open to all',
};

/** States whose slot could be freed. `manual` is excluded on purpose — a bastion
 *  entry has no agent by design, and offering to reclaim it invites an outage. */
export const ATLAS_RECLAIMABLE_STATES: AtlasEntryState[] = ['no_agent', 'stale'];
